import { type FC, useMemo } from 'react'
import { Button, Alert, AlertType } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { useParams } from 'react-router'
import { PageHeader, WelcomeStep } from '../../../components'
import { AccountTypeEnum } from '../../../constants/api/account-type-enum'

interface StepType {
  description: string
  title: string
}

const welcomeContent = [
  {
    title: 'title_brokerage',
    steps: [
      {
        title: 'brokerage_step_1_title',
        description: 'brokerage_step_1_description',
      },
      {
        title: 'brokerage_step_2_title',
        description: 'brokerage_step_2_description',
      },
      {
        title: 'brokerage_step_3_title',
        description: 'brokerage_step_3_description',
      },
    ],
  },
  {
    title: 'title_roth',
    steps: [
      {
        title: 'roth_step_1_title',
        description: 'roth_step_1_description',
      },
      {
        title: 'roth_step_2_title',
        description: 'roth_step_2_description',
      },
      {
        title: 'roth_step_3_title',
        description: 'roth_step_3_description',
      },
    ],
  },
  {
    title: 'title_trad',
    steps: [
      {
        title: 'trad_step_1_title',
        description: 'trad_step_1_description',
      },
      {
        title: 'trad_step_2_title',
        description: 'trad_step_2_description',
      },
      {
        title: 'trad_step_3_title',
        description: 'trad_step_3_description',
      },
    ],
  },
]

export const WelcomeCompanionPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()
  const { accountType } = useParams()

  // Generate content based on accountType
  const content = useMemo(() => {
    switch (accountType) {
      case AccountTypeEnum.BROKERAGE:
        return welcomeContent[0]
      case AccountTypeEnum.ROTH:
        return welcomeContent[1]
      case AccountTypeEnum.TRADITIONAL:
        return welcomeContent[2]
      default:
        return undefined
    }
  }, [accountType])

  const title = useMemo(() => {
    switch (accountType) {
      case AccountTypeEnum.BROKERAGE:
        return t('investments.welcome.title_brokerage')
      case AccountTypeEnum.ROTH:
        return t('investments.welcome.title_roth')
      case AccountTypeEnum.TRADITIONAL:
        return t('investments.welcome.title_trad')
      default:
        return ''
    }
  }, [accountType, t])

  // Submit
  const onSubmit = (): void => {
    navigate('/')
  }

  return (
    <EnrollmentLayout title="Welcome">
      <div className="egw-container-enroll h-full flex flex-col">
        <PageHeader title={title} />
        <div className="flex-1 md:hidden" />

        {/* Non-generic Subheader */}
        <p className="typo-primary-2xl-light mb-md text-text-header">{t('investments.welcome.subheader')}</p>

        {content ? (
          content.steps.map((e: StepType, i: number) => {
            return (
              <WelcomeStep
                number={i + 1}
                title={t(`investments.welcome.${e.title}`)}
                description={t(`investments.welcome.${e.description}`)}
                testId={`step-${i}`}
                key={t(`investments.welcome.${e.title}`).replaceAll(' ', '-')}
              />
            )
          })
        ) : (
          <Alert alertType={AlertType.Error} description={t('no_content')} className="mt-sm" />
        )}

        <Button
          type="submit"
          className="w-full md:w-min mt-lg"
          // isDisabled={isSubmitting}
          // isLoading={isSubmitting}
          testId="enrollment-continue-button"
          onClick={onSubmit}
        >
          {t('common.continue')}
        </Button>
      </div>
    </EnrollmentLayout>
  )
}
